import { JSX, ReactNode, useEffect, useState } from 'react';
import { CardTabs } from 'vopay';
import { useTranslation } from 'react-i18next';
import { withStyles } from 'vopay';
import styles from './ContactDetailView.module.scss';
import CustomerOverview, { OverviewProps } from './CustomerOverview/CustomerOverview';
import DetailsTab from './Details/DetailsTab';
import PaymentMethodsTab, { PaymentMethodsTabProps } from './PaymentMethods/PaymentMethodsTab';
import ContactActivityTab, { ActivityTypeVisibility } from './Activity/ContactActivityTab';
import useContactDetailData, { ContactDetailData } from '#shared/hooks/useContactDetailData';
import useClientAccountDetailData, { ClientAccountDetailData } from '#shared/hooks/useClientAccountDetailData';
import SettingsTab from './SettingsTab';
import usePaymentMethods from '#shared/hooks/useApi/usePaymentMethods';
import useAspContactTabs from '#shared/hooks/useAspContactTabs';
import BalancesCard from './ClientAccountCards/BalancesCard';
import { HostNavigationProvider } from './HostNavigationContext';
import TransactionLimitCard from './ClientAccountCards/TransactionLimitCard';
import { useAlertDialog } from '#shared/providers/AlertDialog';
import AccountingSoftware, {
  AccountingSoftwareHostProps,
} from '#shared/components/AccountingSoftware/AccountingSystemContainer';

import { Skeleton } from '#shared/components/UI';

const cn = withStyles(styles);

export type ContactDetailViewProps = {
  // The linked record: a client account id or a contact id — which family it
  // belongs to comes from the account's ClientAccountsEnabled flag.
  clientAccountId: string;
  clientAccountsEnabled: boolean;
  // Passed through to the embedded Receivables/Payables tabs and mapped onto
  // the payment-methods feature facts.
  accounting: Pick<AccountingSoftwareHostProps, 'account' | 'userPermissions' | 'hostActions'>;
  // Controlled tab for hosts that sync it to their URL (the portal pages'
  // ?cardTab= param). Uncontrolled (in-memory) when omitted — the drawer.
  tab?: string;
  onTabChange?: (tab: string) => void;
  // Host-only tabs appended after the standard set (the portal's cPortal tab).
  extraTabs?: { value: string; trigger: ReactNode; content: ReactNode }[];
  // Per-type visibility for the Activity sub-tables (the portal gates them by
  // module feature flags). Omitted types default to visible.
  activityTypes?: ActivityTypeVisibility;
  // Activity-tab parity inputs for portal hosts: URL-persisted filters (the
  // portal pages keep filter state in the page URL; the drawer must not touch
  // the host URL), the user's preferred timezone (default UTC), and the
  // Include Sub Accounts checkbox gate.
  activityOptions?: {
    persistFiltersInUrl?: boolean;
    timezone?: string;
    showIncludeSubAccounts?: boolean;
  };
  // Host-owned record data: the portal pages fetch once at page level (their
  // chrome reads the same instance, so edits inside the view refresh it too)
  // and inject it here. Omitted = the view fetches internally (the drawer).
  // Pass the one matching the flavour.
  contactDetail?: ContactDetailData;
  clientAccountDetail?: ClientAccountDetailData;
  // The host's accounting feature flag (the portal's
  // AccountingSoftwareIntegrationEnabled locked feature) — false skips the
  // Receivables/Payables tab lookup entirely. The drawer (always inside the
  // accounting feature) omits it.
  accountingEnabled?: boolean;
};

type FlavourBodyProps = Omit<ContactDetailViewProps, 'clientAccountsEnabled'>;

// The contact detail body — the portal's ContactDetail/ClientAccountDetail
// rendered inside the drawer: overview, editable Details, the record's own
// scoped Receivables/Payables, the full Activity tab, and the portal's
// Payment Methods experience. The tab lives in memory (the drawer must not
// rewrite the host URL, unlike the portal pages' cardTab param).
export default function ContactDetailView({
  clientAccountId,
  clientAccountsEnabled,
  ...hostProps
}: ContactDetailViewProps): JSX.Element {
  if (clientAccountsEnabled) {
    return <ClientAccountBody clientAccountId={clientAccountId} {...hostProps} />;
  }

  return <ContactBody clientAccountId={clientAccountId} {...hostProps} />;
}

function LoadingSkeletons(): JSX.Element {
  return (
    <div className={cn('view')}>
      <Skeleton height="96px" />
      <Skeleton height="320px" />
    </div>
  );
}

// Mirrors the portal's Contacts/ContactPage/ContactDetail.tsx.
function ContactBody({
  clientAccountId,
  accounting,
  contactDetail,
  accountingEnabled = true,
  tab,
  onTabChange,
  extraTabs,
  activityTypes,
  activityOptions,
}: FlavourBodyProps): JSX.Element {
  const internalDetail = useContactDetailData(clientAccountId, false, {
    loginUserName: accounting.account.LoginUserName,
    skip: contactDetail !== undefined,
  });

  const {
    contact,
    refreshContact,
    groupedMethods,
    setDefaultMethod,
    deleteMethod,
    deleteGroupedMethod,
    refreshPaymentMethods,
  } = contactDetail ?? internalDetail;

  const {
    resolving: aspTabsResolving,
    receivableContactId,
    payableContactId,
  } = useAspContactTabs(clientAccountId, accountingEnabled);

  if (!contact || aspTabsResolving) return <LoadingSkeletons />;

  return (
    <DetailTabs
      tab={tab}
      onTabChange={onTabChange}
      extraTabs={extraTabs}
      activityTypes={activityTypes}
      activityOptions={activityOptions}
      accounting={accounting}
      clientAccountsEnabled={false}
      receivableContactId={receivableContactId}
      payableContactId={payableContactId}
      settings={{ lang: contact.Language }}
      overview={{
        accountID: contact.ContactID,
        active: contact.IsActive,
        accountName: contact.ContactName,
        lang: contact.Language,
        isBusiness: contact.IsBusiness,
      }}
      detailsAccount={{
        ...contact,
        AccountID: contact.ContactID,
        AccountName: contact.ContactName,
        AdditionalContacts:
          contact.AdditionalContacts.email && contact.AdditionalContacts.email.length > 0
            ? contact.AdditionalContacts.email
            : undefined,
      }}
      onDetailsUpdate={refreshContact}
      paymentMethods={{
        groupedMethods,
        onRefresh: refreshPaymentMethods,
        onSetDefaultMethod: setDefaultMethod,
        onDeleteMethod: deleteMethod,
        onDeleteGroupedMethod: deleteGroupedMethod,
        accountID: contact.ContactID,
        accountName: contact.ContactName,
        email: contact.EmailAddress,
        active: contact.IsActive,
        accountPhone: contact.PhoneNumber,
        accountAddress1: contact.Address1,
        accountCity: contact.City,
        accountProvince: contact.Province,
        accountPostalCode: contact.PostalCode,
        accountCountry: contact.Country,
      }}
    />
  );
}

// Mirrors the portal's ClientAccounts/ClientAccountPage/ClientAccountDetail.tsx
// (minus the portal-page extras: balance/limit cards and the cPortal tab,
// which need portal-only providers).
function ClientAccountBody({
  clientAccountId,
  accounting,
  clientAccountDetail,
  accountingEnabled = true,
  tab,
  onTabChange,
  extraTabs,
  activityTypes,
  activityOptions,
}: FlavourBodyProps): JSX.Element {
  const alertDialog = useAlertDialog();
  const { t: tShared } = useTranslation('translations', { keyPrefix: 'Shared' });

  const internalDetail = useClientAccountDetailData(clientAccountId, { skip: clientAccountDetail !== undefined });
  const { clientAccount, refreshClientAccount } = clientAccountDetail ?? internalDetail;

  const { groupedMethods, fetchMethods, setDefaultMethod, deleteMethod, deleteGroupedMethod } = usePaymentMethods({
    accountID: clientAccountId,
    currencies: ['CAD'],
    clientAccountsEnabled: true,
    loginUserName: accounting.account.LoginUserName,
  });

  const {
    resolving: aspTabsResolving,
    receivableContactId,
    payableContactId,
  } = useAspContactTabs(clientAccountId, accountingEnabled);

  async function refreshPaymentMethods() {
    try {
      const res = await fetchMethods();

      return res;
    } catch (err) {
      const msg = err instanceof Error ? err.message : `Couldn't fetch payment methods for ${clientAccountId}`;
      alertDialog.error(tShared('error', { defaultValue: 'Error' }), msg);
    }
  }

  useEffect(() => {
    refreshPaymentMethods();
  }, []);

  if (!clientAccount || aspTabsResolving) return <LoadingSkeletons />;

  return (
    <DetailTabs
      tab={tab}
      onTabChange={onTabChange}
      extraTabs={extraTabs}
      activityTypes={activityTypes}
      activityOptions={activityOptions}
      accounting={accounting}
      clientAccountsEnabled
      receivableContactId={receivableContactId}
      payableContactId={payableContactId}
      overview={{
        accountID: clientAccount.ClientAccountID,
        active: clientAccount.IsActive,
        accountName: clientAccount.ClientName,
        lang: clientAccount.Language,
        isBusiness: clientAccount.IsBusiness,
      }}
      detailsAccount={{
        ...clientAccount,
        AccountID: clientAccount.ClientAccountID,
        AccountName: clientAccount.ClientName,
        AdditionalContacts:
          clientAccount.AdditionalContacts.email && clientAccount.AdditionalContacts.email.length > 0
            ? clientAccount.AdditionalContacts.email
            : undefined,
      }}
      onDetailsUpdate={refreshClientAccount}
      settings={{ lang: clientAccount.Language, receiveOnly: clientAccount.ClientType === 'receive-only' }}
      overviewExtras={
        <>
          {clientAccount.Balances && (
            <BalancesCard balances={clientAccount.Balances} country={accounting.account.Country} />
          )}

          <TransactionLimitCard
            currencies={
              clientAccount.Balances?.length ? clientAccount.Balances.map(({ Currency }) => Currency) : ['CAD']
            }
            limit={clientAccount.TransactionLimit}
            country={accounting.account.Country}
          />
        </>
      }
      paymentMethods={{
        groupedMethods,
        onRefresh: refreshPaymentMethods,
        onSetDefaultMethod: setDefaultMethod,
        onDeleteMethod: deleteMethod,
        onDeleteGroupedMethod: deleteGroupedMethod,
        accountID: clientAccount.ClientAccountID,
        accountName: clientAccount.ClientName,
        email: clientAccount.EmailAddress,
        active: clientAccount.IsActive,
      }}
    />
  );
}

type DetailTabsProps = Pick<
  ContactDetailViewProps,
  'tab' | 'onTabChange' | 'extraTabs' | 'activityTypes' | 'activityOptions'
> & {
  accounting: ContactDetailViewProps['accounting'];
  // The (trigger-less) settings pane, reachable via a controlled tab value of
  // 'settings' — mirrors the portal's commented-out Settings trigger whose
  // content stays mounted.
  settings: { lang: string; receiveOnly?: boolean };
  // Extra header cards beside the overview (the client-account flavour's
  // balances and transaction limit, per the portal page and the Figma drawer).
  overviewExtras?: ReactNode;
  clientAccountsEnabled: boolean;
  receivableContactId: number | undefined;
  payableContactId: number | undefined;
  overview: Omit<
    OverviewProps,
    'clientAccountsEnabled' | 'hasManageContactsPermission' | 'hasManageClientAccountsPermission'
  >;
  detailsAccount: Parameters<typeof DetailsTab>[0]['account'];
  onDetailsUpdate: () => Promise<unknown>;
  paymentMethods: Omit<
    PaymentMethodsTabProps,
    | 'clientAccountsEnabled'
    | 'country'
    | 'loginUserName'
    | 'senderName'
    | 'isPayLinkEnabled'
    | 'isIQ11DataEnrichingEnabled'
    | 'isIQ11DataEnrichingEnhancedEnabled'
    | 'lockedSelectOptions'
    | 'hasViewBankAccountTransactionHistoryPermission'
    | 'hasViewElinxSettingsPermission'
    | 'hasCreateElinxPayPermission'
    | 'hasCreateElinxCollectPermission'
    | 'hasCreateElinxConnectPermission'
    | 'hasManageContactsPermission'
    | 'hasManageClientAccountsPermission'
    | 'isMicroDepositBankAccountVerificationEnabled'
    | 'isInteracBasicVerificationEnabled'
    | 'isInteracEnhancedVerificationEnabled'
    | 'isCardBasicVerificationEnabled'
    | 'isCardEnhancedVerificationEnabled'
    | 'onLockedFeature'
  >;
};

function DetailTabs({
  accounting,
  overviewExtras,
  tab: controlledTab,
  onTabChange,
  extraTabs,
  activityTypes,
  activityOptions,
  settings,
  clientAccountsEnabled,
  receivableContactId,
  payableContactId,
  overview,
  detailsAccount,
  onDetailsUpdate,
  paymentMethods,
}: DetailTabsProps): JSX.Element {
  const [uncontrolledTab, setUncontrolledTab] = useState('details');
  const tab = controlledTab ?? uncontrolledTab;

  const handleTabChange = (next: string) => {
    setUncontrolledTab(next);
    onTabChange?.(next);
  };

  const { t: tCustomer } = useTranslation('translations', { keyPrefix: 'Shared.Customers' });
  const { t: tAcct } = useTranslation('translations', { keyPrefix: 'Shared.AccountingSystem' });

  const { account, hostActions } = accounting;
  const hasManageContactsPermission = accounting.userPermissions?.ManageContacts ?? false;
  const hasManageClientAccountsPermission = accounting.userPermissions?.ManageClientAccounts ?? false;

  return (
    <HostNavigationProvider onNavigate={hostActions?.onNavigate}>
      <div className={cn('view')}>
        <div className={cn('overviewRow')}>
          <CustomerOverview
            {...overview}
            clientAccountsEnabled={clientAccountsEnabled}
            hasManageContactsPermission={hasManageContactsPermission}
            hasManageClientAccountsPermission={hasManageClientAccountsPermission}
          />

          {overviewExtras}
        </div>

        <CardTabs.Root value={tab} onValueChange={handleTabChange}>
          <CardTabs.List aria-label={tCustomer('manageYourAccount', { defaultValue: 'Manage your account' })}>
            <CardTabs.Trigger value="details">{tCustomer('details', { defaultValue: 'Details' })}</CardTabs.Trigger>

            {receivableContactId !== undefined && (
              <CardTabs.Trigger value="receivables">
                {tAcct('pageTitleReceivable', { defaultValue: 'Receivables' })}
              </CardTabs.Trigger>
            )}

            {payableContactId !== undefined && (
              <CardTabs.Trigger value="payables">
                {tAcct('pageTitlePayable', { defaultValue: 'Payables' })}
              </CardTabs.Trigger>
            )}

            <CardTabs.Trigger value="activity">{tCustomer('activity', { defaultValue: 'Activity' })}</CardTabs.Trigger>

            <CardTabs.Trigger value="paymentMethods">
              {tCustomer('paymentMethods', { defaultValue: 'Payment Methods' })}
            </CardTabs.Trigger>

            {extraTabs?.map(({ value, trigger }) => (
              <CardTabs.Trigger key={value} value={value}>
                {trigger}
              </CardTabs.Trigger>
            ))}
          </CardTabs.List>

          <CardTabs.Content value="details">
            <DetailsTab
              account={detailsAccount}
              onUpdate={onDetailsUpdate}
              clientAccountsEnabled={clientAccountsEnabled}
              hasManageContactsPermission={hasManageContactsPermission}
              hasManageClientAccountsPermission={hasManageClientAccountsPermission}
            />
          </CardTabs.Content>

          <CardTabs.Content value="activity">
            <ContactActivityTab
              clientAccountId={paymentMethods.accountID}
              clientAccountsEnabled={clientAccountsEnabled}
              country={account.Country}
              active={overview.active}
              activityTypes={activityTypes}
              persistFiltersInUrl={activityOptions?.persistFiltersInUrl}
              timezone={activityOptions?.timezone}
              showIncludeSubAccounts={activityOptions?.showIncludeSubAccounts}
            />
          </CardTabs.Content>

          <CardTabs.Content value="paymentMethods">
            <PaymentMethodsTab
              {...paymentMethods}
              clientAccountsEnabled={clientAccountsEnabled}
              country={account.Country}
              loginUserName={account.LoginUserName}
              senderName={account.AccountName}
              isPayLinkEnabled={account.PayLinkEnabled}
              isIQ11DataEnrichingEnabled={account.IQ11DataEnrichingEnabled}
              isIQ11DataEnrichingEnhancedEnabled={account.IQ11DataEnrichingEnhancedEnabled}
              lockedSelectOptions={account.LockedSelectOptions}
              hasViewBankAccountTransactionHistoryPermission={
                accounting.userPermissions?.ViewBankAccountTransactionHistory ?? false
              }
              hasViewElinxSettingsPermission={accounting.userPermissions?.ViewElinxSettings ?? false}
              hasCreateElinxPayPermission={accounting.userPermissions?.CreateElinxPay ?? false}
              hasCreateElinxCollectPermission={accounting.userPermissions?.CreateElinxCollect ?? false}
              hasCreateElinxConnectPermission={accounting.userPermissions?.CreateElinxConnect ?? false}
              hasManageContactsPermission={hasManageContactsPermission}
              hasManageClientAccountsPermission={hasManageClientAccountsPermission}
              isMicroDepositBankAccountVerificationEnabled={account.MicroDepositBankAccountVerificationEnabled}
              isInteracBasicVerificationEnabled={account.InteracBasicVerificationEnabled}
              isInteracEnhancedVerificationEnabled={account.InteracEnhancedVerificationEnabled}
              isCardBasicVerificationEnabled={account.CardBasicVerificationEnabled}
              isCardEnhancedVerificationEnabled={account.CardEnhancedVerificationEnabled}
              onLockedFeature={hostActions?.onLockedFeature}
            />
          </CardTabs.Content>

          {receivableContactId !== undefined && (
            <CardTabs.Content value="receivables">
              <AccountingSoftware Type="Receivable" ContactID={receivableContactId} {...accounting} />
            </CardTabs.Content>
          )}

          {payableContactId !== undefined && (
            <CardTabs.Content value="payables">
              <AccountingSoftware Type="Payable" ContactID={payableContactId} {...accounting} />
            </CardTabs.Content>
          )}

          {extraTabs?.map(({ value, content }) => (
            <CardTabs.Content key={value} value={value}>
              {content}
            </CardTabs.Content>
          ))}

          {/* No Settings trigger (commented out in the portal too), but the
              content stays reachable through a controlled tab of 'settings'
              (the portal pages' ?cardTab=settings). */}
          <CardTabs.Content value="settings">
            <SettingsTab
              lang={settings.lang}
              receiveOnly={settings.receiveOnly}
              clientAccountsEnabled={clientAccountsEnabled}
            />
          </CardTabs.Content>
        </CardTabs.Root>
      </div>
    </HostNavigationProvider>
  );
}
